import { getStorage, ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject, updateMetadata } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { app } from './config';

const storage = getStorage(app);

// Max image size after compression: 5MB
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_IMAGE_DIMENSION = 2560; // Prevent extremely large dimensions

async function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = String(reader.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function drawToCanvas(img: HTMLImageElement, maxDimension: number): HTMLCanvasElement {
  const { naturalWidth, naturalHeight } = img;
  let targetWidth = naturalWidth;
  let targetHeight = naturalHeight;

  if (Math.max(naturalWidth, naturalHeight) > maxDimension) {
    const scale = maxDimension / Math.max(naturalWidth, naturalHeight);
    targetWidth = Math.round(naturalWidth * scale);
    targetHeight = Math.round(naturalHeight * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get 2D context for canvas');
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
  return canvas;
}

async function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return await new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error('Failed to create blob from canvas'));
      resolve(blob);
    }, type, quality);
  });
}

/**
 * Compress an image file to be <= MAX_IMAGE_BYTES with reasonable dimensions.
 * Returns the original file if it is already below the threshold and not huge.
 */
export async function compressImageToFiveMB(file: File): Promise<File> {
  try {
    // If it's already small enough, skip to save CPU
    if (file.size <= MAX_IMAGE_BYTES && file.type.startsWith('image/')) {
      return file;
    }

    const img = await loadImageFromFile(file);
    const canvas = drawToCanvas(img, MAX_IMAGE_DIMENSION);

    // Prefer JPEG for better compression; preserve PNG only if type is PNG and small alpha usage would be lost.
    const outputType = 'image/jpeg';

    // Try a few quality levels to get under target size
    const qualitySteps = [0.9, 0.82, 0.75, 0.68, 0.6, 0.52, 0.45, 0.38, 0.3];
    for (const q of qualitySteps) {
      const blob = await canvasToBlob(canvas, outputType, q);
      if (blob.size <= MAX_IMAGE_BYTES) {
        return new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: outputType });
      }
    }

    // As a fallback, downscale further and try once more
    const smallerCanvas = drawToCanvas(img, Math.min(1600, MAX_IMAGE_DIMENSION));
    const blob = await canvasToBlob(smallerCanvas, outputType, 0.7);
    if (blob.size <= MAX_IMAGE_BYTES) {
      return new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: outputType });
    }

    // Still too big
    throw new Error('Image is too large even after compression. Max size is 5MB.');
  } catch (error) {
    // If compression fails for any reason, and original file is already within limits, return it. Otherwise, rethrow.
    if (file.size <= MAX_IMAGE_BYTES) return file;
    throw error;
  }
}

export async function uploadImage(file: File, path: string): Promise<string> {
  try {
    console.log('[uploadImage] Starting upload for file:', file.name);
    console.log('[uploadImage] Target path:', path);

    // Compress file to be within limits
    const compressedFile = await compressImageToFiveMB(file);

    const fileExtension = (compressedFile.name.split('.').pop()) || (file.name.split('.').pop());
    const fileName = `${uuidv4()}.${fileExtension}`;
    const fullPath = `${path}/${fileName}`;
    console.log('[uploadImage] Full path:', fullPath);

    const storageRef = ref(storage, fullPath);
    console.log('[uploadImage] Created storage reference');

    const snapshot = await uploadBytes(storageRef, compressedFile, { contentType: compressedFile.type });
    console.log('[uploadImage] File uploaded successfully');

    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('[uploadImage] Got download URL:', downloadURL);

    // Store the full path in the metadata
    const metadata = {
      customMetadata: {
        fullPath
      }
    };
    await updateMetadata(storageRef, metadata);
    console.log('[uploadImage] Updated metadata with full path');

    return downloadURL;
  } catch (error: any) {
    console.error('[uploadImage] Error uploading image:', error);
    throw new Error(error.message || 'Failed to upload image');
  }
}

export async function uploadImageWithProgress(
  file: File,
  path: string,
  onProgress: (progressPercent: number) => void
): Promise<string> {
  try {
    // Compress file to be within limits
    const compressedFile = await compressImageToFiveMB(file);

    const fileExtension = (compressedFile.name.split('.').pop()) || (file.name.split('.').pop());
    const fileName = `${uuidv4()}.${fileExtension}`;
    const fullPath = `${path}/${fileName}`;

    const storageRef = ref(storage, fullPath);
    const uploadTask = uploadBytesResumable(storageRef, compressedFile, { contentType: compressedFile.type });

    return await new Promise<string>((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          try { onProgress(progress); } catch { }
        },
        (error) => {
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            const metadata = { customMetadata: { fullPath } } as const;
            await updateMetadata(storageRef, metadata);
            resolve(downloadURL);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  } catch (error: any) {
    console.error('[uploadImageWithProgress] Error uploading image:', error);
    throw new Error(error.message || 'Failed to upload image');
  }
}

// Upload a raw video file to Firebase Storage and return its download URL
export async function uploadVideo(
  file: File,
  path: string,
  onProgress?: (progressPercent: number) => void
): Promise<string> {
  try {
    const fileExtension = file.name.split('.').pop();
    const fileName = `${uuidv4()}.${fileExtension}`;
    const fullPath = `${path}/${fileName}`;

    const storageRef = ref(storage, fullPath);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return await new Promise<string>((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          if (onProgress) {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            try { onProgress(progress); } catch { }
          }
        },
        (error) => reject(error),
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            const metadata = { customMetadata: { fullPath } } as const;
            await updateMetadata(storageRef, metadata);
            resolve(downloadURL);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  } catch (error: any) {
    console.error('[uploadVideo] Error uploading video:', error);
    throw new Error(error.message || 'Failed to upload video');
  }
}

export async function deleteImage(path: string): Promise<void> {
  try {
    console.log('[deleteImage] Attempting to delete image at path:', path);
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
    console.log('[deleteImage] Successfully deleted image');
  } catch (error: any) {
    console.error('[deleteImage] Error deleting image:', error);
    throw new Error(error.message || 'Failed to delete image');
  }
}